import React from "react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  formatCompactCurrency,
  formatCompactNumber,
  formatFullCurrency,
  formatFullNumber,
} from "@/lib/format";

type KpiCardProps = {
  label: string;
  value: React.ReactNode;
  trend?: React.ReactNode;
  /** Percentage change shown beside the value (green up / red down). */
  trendPercent?: number;
  icon?: React.ReactNode;
  iconBg?: string;
  /** When set and `value` is a number, format as compact currency. */
  currency?: string;
  compact?: boolean;
  className?: string;
};

export const KpiCard: React.FC<KpiCardProps> = ({
  label,
  value,
  icon,
  trend,
  trendPercent,
  currency,
  compact = true,
  className,
}) => {
  const numeric = typeof value === "number" && Number.isFinite(value);
  const display = numeric
    ? compact
      ? currency
        ? formatCompactCurrency(value, currency)
        : formatCompactNumber(value)
      : currency
        ? formatFullCurrency(value, currency)
        : formatFullNumber(value)
    : value;

  const fullTitle = numeric
    ? currency
      ? formatFullCurrency(value, currency)
      : formatFullNumber(value)
    : typeof value === "string"
      ? value
      : undefined;

  const showTrend =
    typeof trendPercent === "number" && Number.isFinite(trendPercent);
  const up = showTrend && trendPercent >= 0;

  return (
    <Card
      className={cn(
        "min-h-[148px] min-w-0 overflow-hidden rounded-[1.25rem] border border-border bg-card p-5 shadow-none transition-colors hover:border-border",
        className,
      )}
    >
      <div className="flex h-full min-w-0 flex-col">
        {icon ? (
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-muted text-muted-foreground [&>svg]:h-[18px] [&>svg]:w-[18px]">
            {icon}
          </span>
        ) : null}

        <div className="mt-4 flex min-w-0 items-end gap-1.5">
          <span
            className="min-w-0 truncate font-heading text-[1.85rem] font-bold leading-none tracking-tight text-foreground tabular-nums xl:text-[2rem]"
            title={fullTitle}
          >
            {display}
          </span>
          {showTrend ? (
            <span
              className={cn(
                "mb-0.5 shrink-0 text-sm font-semibold leading-none",
                up ? "text-emerald-600" : "text-red-500",
              )}
            >
              {Math.abs(trendPercent).toFixed(0)}%{up ? " ↑" : " ↓"}
            </span>
          ) : trend ? (
            <span className="mb-0.5 min-w-0 shrink truncate text-sm font-semibold leading-none">
              {trend}
            </span>
          ) : null}
        </div>

        <p className="mt-auto pt-3 text-sm font-medium text-muted-foreground">
          {label}
        </p>
      </div>
    </Card>
  );
};

export default KpiCard;
